import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

const frameThickness = 60

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

function validateDimension(type: string, value: number): number {
  const rules: Record<string, { min: number; max: number }> = {
    totalWidth: { min: 2000, max: 10000 },
    totalHeight: { min: 1800, max: 5000 },
    leftFixedWidth: { min: 200, max: 5000 },
    centerDoorsWidth: { min: 1000, max: 5000 },
    rightFixedWidth: { min: 200, max: 5000 },
    doorPanelHeight: { min: 1200, max: 5000 },
    windowHeight: { min: 500, max: 5000 },
  }
  const rule = rules[type] || { min: 100, max: 10000 }
  return Math.max(rule.min, Math.min(rule.max, value))
}

function updateHingesProportionally(
  model: ModelData,
  frameId: string,
  oldFrameHeight: number,
  newFrameHeight: number,
) {
  const frame = getParam(model.parameters!, frameId)
  if (!frame?.hardware?.hinges) return

  const updatedHinges = frame.hardware.hinges.map((hinge) => {
    const isBottomHinge1 = hinge.positionId?.includes('bottom_hinge_1')
    const isBottomHinge2 = hinge.positionId?.includes('bottom_hinge_2')
    const relativeY = oldFrameHeight > 0 ? hinge.position.y / oldFrameHeight : 0
    let newY: number
    if (isBottomHinge1) {
      newY = Math.round(newFrameHeight * 0.75)
    } else if (isBottomHinge2) {
      newY = Math.round(newFrameHeight * 0.85)
    } else {
      newY = Math.max(60, Math.min(newFrameHeight - hinge.height - 60, relativeY * newFrameHeight))
    }
    return { ...hinge, position: { ...hinge.position, y: newY } }
  })

  updateParam(model, frameId, {
    hardware: { ...frame.hardware, hinges: updatedHinges },
  })
}

function centerHandles(model: ModelData, frameId: string, newFrameHeight: number) {
  const frame = getParam(model.parameters!, frameId)
  if (!frame?.hardware?.handles) return

  const updatedHandles = frame.hardware.handles.map((handle) => {
    const newY = (newFrameHeight - handle.height) / 2
    return { ...handle, position: { ...handle.position, y: newY } }
  })

  updateParam(model, frameId, {
    hardware: { ...frame.hardware, handles: updatedHandles },
  })
}

/**
 * Redistributive height update: when one panel grows, its paired panel shrinks.
 * Right side: openable (top_window_2) + fixed (right_glass_panel)
 * Left side: openable (top_window_1) + fixed (left_glass_panel)
 */
function handleRedistributiveHeight(
  model: ModelData,
  updateType: string,
  newHeight: number,
) {
  const params = model.parameters!

  switch (updateType) {
    case 'rightOpenablePanelHeight': {
      const topFrameProfile = getParam(params, 'top_frame_profile')
      const windowFrameRight = getParam(params, 'top_window_2_right_frame_profile')
      const rightOpenable = getParam(params, 'right_glass_panel_opanable')
      const rightFixed = getParam(params, 'right_glass_panel')
      const middleHorizontal2 = getParam(params, 'middle_horizontal_frame_profile_2')
      if (!windowFrameRight || !rightFixed || !middleHorizontal2 || !topFrameProfile) break

      const ft = topFrameProfile.height || frameThickness
      const currentOpenableHeight = (windowFrameRight.height || 0) + ft + (middleHorizontal2.height || 0)
      const heightDifference = newHeight - currentOpenableHeight
      const newWindowFrameHeight = (windowFrameRight.height || 0) + heightDifference
      const newOpenableGlassHeight = newWindowFrameHeight - ft - ft
      const newFixedPanelHeight = Math.max(200, (rightFixed.height || 0) - heightDifference)
      const newMiddleFrameY = ft + newWindowFrameHeight
      const newRightFixedY = newMiddleFrameY + ft
      const windowBottomY = ft + newWindowFrameHeight - ft

      updateParam(model, 'top_window_2_left_frame_profile', { height: newWindowFrameHeight })
      updateParam(model, 'top_window_2_right_frame_profile', { height: newWindowFrameHeight })

      const topWindow2Bottom = getParam(params, 'top_window_2_bottom_frame_profile')
      updateParam(model, 'top_window_2_bottom_frame_profile', {
        position: { x: topWindow2Bottom?.position?.x || ft, y: windowBottomY, z: 0 } as ModelParameter['position'],
      })
      updateParam(model, 'right_glass_panel_opanable', {
        height: newOpenableGlassHeight,
        position: { x: rightOpenable?.position?.x || 120, y: ft + ft, z: 0 } as ModelParameter['position'],
      })
      updateParam(model, 'right_glass_panel', {
        height: newFixedPanelHeight,
        position: { ...rightFixed.position, y: newRightFixedY } as ModelParameter['position'],
      })
      updateParam(model, 'middle_horizontal_frame_profile_2', {
        position: { ...middleHorizontal2.position, y: newMiddleFrameY } as ModelParameter['position'],
      })

      const oldFrameHeight = windowFrameRight.height || 980
      updateHingesProportionally(model, 'top_window_2_right_frame_profile', oldFrameHeight, newWindowFrameHeight)
      centerHandles(model, 'top_window_2_left_frame_profile', newWindowFrameHeight)
      break
    }

    case 'rightFixedPanelHeight': {
      const topFrameProfile = getParam(params, 'top_frame_profile')
      const windowFrameRight = getParam(params, 'top_window_2_right_frame_profile')
      const rightOpenable = getParam(params, 'right_glass_panel_opanable')
      const rightFixed = getParam(params, 'right_glass_panel')
      const middleHorizontal2 = getParam(params, 'middle_horizontal_frame_profile_2')
      const bottomRightFrame = getParam(params, 'bottom_right_frame_profile')
      if (!windowFrameRight || !rightFixed || !middleHorizontal2 || !topFrameProfile || !bottomRightFrame) break

      const ft = topFrameProfile.height || frameThickness
      const currentFixedHeight = (rightFixed.height || 0) + (bottomRightFrame.height || 0)
      const heightDifference = newHeight - currentFixedHeight
      const newFixedPanelHeight = (rightFixed.height || 0) + heightDifference
      const newWindowFrameHeight = Math.max(200, (windowFrameRight.height || 0) - heightDifference)
      const newOpenableGlassHeight = newWindowFrameHeight - ft - ft
      const newMiddleFrameY = ft + newWindowFrameHeight
      const newRightFixedY = newMiddleFrameY + ft
      const windowBottomY = ft + newWindowFrameHeight - ft

      updateParam(model, 'top_window_2_left_frame_profile', { height: newWindowFrameHeight })
      updateParam(model, 'top_window_2_right_frame_profile', { height: newWindowFrameHeight })

      const topWindow2Bottom = getParam(params, 'top_window_2_bottom_frame_profile')
      updateParam(model, 'top_window_2_bottom_frame_profile', {
        position: { x: topWindow2Bottom?.position?.x || ft, y: windowBottomY, z: 0 } as ModelParameter['position'],
      })
      updateParam(model, 'right_glass_panel_opanable', {
        height: newOpenableGlassHeight,
        position: { x: rightOpenable?.position?.x || 120, y: ft + ft, z: 0 } as ModelParameter['position'],
      })
      updateParam(model, 'right_glass_panel', {
        height: newFixedPanelHeight,
        position: { ...rightFixed.position, y: newRightFixedY } as ModelParameter['position'],
      })
      updateParam(model, 'middle_horizontal_frame_profile_2', {
        position: { ...middleHorizontal2.position, y: newMiddleFrameY } as ModelParameter['position'],
      })

      const oldFrameHeight = windowFrameRight.height || 980
      updateHingesProportionally(model, 'top_window_2_right_frame_profile', oldFrameHeight, newWindowFrameHeight)
      centerHandles(model, 'top_window_2_left_frame_profile', newWindowFrameHeight)
      break
    }

    case 'leftOpenablePanelHeight': {
      const topFrameProfile = getParam(params, 'top_frame_profile')
      const windowFrameLeft = getParam(params, 'top_window_1_left_frame_profile')
      const leftOpenable = getParam(params, 'left_glass_panel_opanable')
      const leftFixed = getParam(params, 'left_glass_panel')
      const middleHorizontal1 = getParam(params, 'middle_horizontal_frame_profile_1')
      if (!windowFrameLeft || !leftFixed || !middleHorizontal1 || !topFrameProfile) break

      const ft = topFrameProfile.height || frameThickness
      const currentOpenableHeight = (windowFrameLeft.height || 0) + ft + (middleHorizontal1.height || 0)
      const heightDifference = newHeight - currentOpenableHeight
      const newWindowFrameHeight = (windowFrameLeft.height || 0) + heightDifference
      const newOpenableGlassHeight = newWindowFrameHeight - ft - ft
      const newFixedPanelHeight = Math.max(200, (leftFixed.height || 0) - heightDifference)
      const newMiddleFrameY = ft + newWindowFrameHeight
      const newLeftFixedY = newMiddleFrameY + ft
      const windowBottomY = ft + newWindowFrameHeight - ft

      updateParam(model, 'top_window_1_left_frame_profile', { height: newWindowFrameHeight })
      updateParam(model, 'top_window_1_right_frame_profile', { height: newWindowFrameHeight })

      const topWindow1Bottom = getParam(params, 'top_window_1_bottom_frame_profile')
      updateParam(model, 'top_window_1_bottom_frame_profile', {
        position: { x: topWindow1Bottom?.position?.x || ft, y: windowBottomY, z: 0 } as ModelParameter['position'],
      })
      updateParam(model, 'left_glass_panel_opanable', {
        height: newOpenableGlassHeight,
        position: { x: leftOpenable?.position?.x || 120, y: ft + ft, z: 0 } as ModelParameter['position'],
      })
      updateParam(model, 'left_glass_panel', {
        height: newFixedPanelHeight,
        position: { ...leftFixed.position, y: newLeftFixedY } as ModelParameter['position'],
      })
      updateParam(model, 'middle_horizontal_frame_profile_1', {
        position: { ...middleHorizontal1.position, y: newMiddleFrameY } as ModelParameter['position'],
      })

      const oldFrameHeight = windowFrameLeft.height || 980
      updateHingesProportionally(model, 'top_window_1_left_frame_profile', oldFrameHeight, newWindowFrameHeight)
      centerHandles(model, 'top_window_1_right_frame_profile', newWindowFrameHeight)
      break
    }

    case 'leftFixedPanelHeight': {
      const topFrameProfile = getParam(params, 'top_frame_profile')
      const windowFrameLeft = getParam(params, 'top_window_1_left_frame_profile')
      const leftOpenable = getParam(params, 'left_glass_panel_opanable')
      const leftFixed = getParam(params, 'left_glass_panel')
      const middleHorizontal1 = getParam(params, 'middle_horizontal_frame_profile_1')
      const bottomLeftFrame = getParam(params, 'bottom_left_frame_profile')
      if (!windowFrameLeft || !leftFixed || !middleHorizontal1 || !topFrameProfile || !bottomLeftFrame) break

      const ft = topFrameProfile.height || frameThickness
      const currentFixedHeight = (leftFixed.height || 0) + (bottomLeftFrame.height || 0)
      const heightDifference = newHeight - currentFixedHeight
      const newFixedPanelHeight = (leftFixed.height || 0) + heightDifference
      const newWindowFrameHeight = Math.max(200, (windowFrameLeft.height || 0) - heightDifference)
      const newOpenableGlassHeight = newWindowFrameHeight - ft - ft
      const newMiddleFrameY = ft + newWindowFrameHeight
      const newLeftFixedY = newMiddleFrameY + ft
      const windowBottomY = ft + newWindowFrameHeight - ft

      updateParam(model, 'top_window_1_left_frame_profile', { height: newWindowFrameHeight })
      updateParam(model, 'top_window_1_right_frame_profile', { height: newWindowFrameHeight })

      const topWindow1Bottom = getParam(params, 'top_window_1_bottom_frame_profile')
      updateParam(model, 'top_window_1_bottom_frame_profile', {
        position: { x: topWindow1Bottom?.position?.x || ft, y: windowBottomY, z: 0 } as ModelParameter['position'],
      })
      updateParam(model, 'left_glass_panel_opanable', {
        height: newOpenableGlassHeight,
        position: { x: leftOpenable?.position?.x || 120, y: ft + ft, z: 0 } as ModelParameter['position'],
      })
      updateParam(model, 'left_glass_panel', {
        height: newFixedPanelHeight,
        position: { ...leftFixed.position, y: newLeftFixedY } as ModelParameter['position'],
      })
      updateParam(model, 'middle_horizontal_frame_profile_1', {
        position: { ...middleHorizontal1.position, y: newMiddleFrameY } as ModelParameter['position'],
      })

      const oldFrameHeight = windowFrameLeft.height || 980
      updateHingesProportionally(model, 'top_window_1_left_frame_profile', oldFrameHeight, newWindowFrameHeight)
      centerHandles(model, 'top_window_1_right_frame_profile', newWindowFrameHeight)
      break
    }
  }
}

const totalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const validatedValue = validateDimension('totalWidth', numValue)

  model.width = validatedValue
  updateParam(model, 'top_frame_profile', { width: validatedValue })

  if (!cascadeUpdates) return model

  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomCenter = getParam(model.parameters!, 'bottom_center_frame_profile_1')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')

  const currentLeftWidth = bottomLeft?.width || 700
  const currentCenterWidth = bottomCenter?.width || 2200
  const currentRightWidth = bottomRight?.width || 700
  const currentTotalWidth = currentLeftWidth + currentCenterWidth + currentRightWidth

  const widthDifference = validatedValue - currentTotalWidth
  const equalAdjustment = Math.floor(widthDifference / 3)
  const remainder = widthDifference - equalAdjustment * 3

  const newLeftWidth = Math.max(200, currentLeftWidth + equalAdjustment)
  const newCenterWidth = Math.max(1000, currentCenterWidth + equalAdjustment)
  const newRightWidth = Math.max(200, currentRightWidth + equalAdjustment + remainder)

  // Update left section
  updateParam(model, 'bottom_left_frame_profile', { width: newLeftWidth })

  const leftWidthAdjustment = newLeftWidth - currentLeftWidth
  const leftOpenable = getParam(model.parameters!, 'left_glass_panel_opanable')
  const leftFixed = getParam(model.parameters!, 'left_glass_panel')
  const middleHorizontal1 = getParam(model.parameters!, 'middle_horizontal_frame_profile_1')

  const newLeftOpenableWidth = (leftOpenable?.width || 460) + leftWidthAdjustment
  const newLeftFixedWidth = (leftFixed?.width || 580) + leftWidthAdjustment
  const newMiddleHorizontal1Width = newLeftFixedWidth

  updateParam(model, 'left_glass_panel_opanable', {
    width: newLeftOpenableWidth,
    position: { x: leftOpenable?.position?.x || 120, y: leftOpenable?.position?.y || 120, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'left_glass_panel', {
    width: newLeftFixedWidth,
    position: { x: frameThickness, y: leftFixed?.position?.y || 1100, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_horizontal_frame_profile_1', {
    width: newMiddleHorizontal1Width,
    position: { x: frameThickness, y: middleHorizontal1?.position?.y || 1040, z: 0 } as ModelParameter['position'],
  })

  // Left section window frames
  updateParam(model, 'top_window_1_left_frame_profile', {
    position: { x: frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_right_frame_profile', {
    position: { x: 120 + newLeftOpenableWidth, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_top_frame_profile', {
    width: newLeftFixedWidth,
    position: { x: frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_bottom_frame_profile', {
    width: newLeftFixedWidth,
    position: {
      x: frameThickness,
      y: getParam(model.parameters!, 'top_window_1_bottom_frame_profile')?.position?.y || 980,
      z: 0,
    } as ModelParameter['position'],
  })

  // Update center section
  const centerStartX = newLeftWidth
  const doorWidth = (newCenterWidth - frameThickness) / 2

  updateParam(model, 'bottom_center_frame_profile_1', {
    width: newCenterWidth,
    position: { x: centerStartX, y: (model.height as number) - frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_glass_panel_1', {
    width: doorWidth,
    position: { x: centerStartX, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_glass_panel_2', {
    width: doorWidth,
    position: { x: centerStartX + doorWidth + frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_vertical_frame_profile_center', {
    position: { x: centerStartX + doorWidth, y: frameThickness, z: 0 } as ModelParameter['position'],
  })

  // Update right section
  const rightStartX = centerStartX + newCenterWidth

  updateParam(model, 'bottom_right_frame_profile', {
    width: newRightWidth,
    position: { x: rightStartX, y: (model.height as number) - frameThickness, z: 0 } as ModelParameter['position'],
  })

  const rightWidthAdjustment = newRightWidth - currentRightWidth
  const rightOpenable = getParam(model.parameters!, 'right_glass_panel_opanable')
  const rightFixed = getParam(model.parameters!, 'right_glass_panel')
  const middleHorizontal2 = getParam(model.parameters!, 'middle_horizontal_frame_profile_2')

  const newRightOpenableWidth = (rightOpenable?.width || 460) + rightWidthAdjustment
  const newRightFixedWidth = (rightFixed?.width || 580) + rightWidthAdjustment
  const newMiddleHorizontal2Width = newRightFixedWidth

  updateParam(model, 'right_glass_panel_opanable', {
    width: newRightOpenableWidth,
    position: { x: rightStartX + 120, y: rightOpenable?.position?.y || 120, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_glass_panel', {
    width: newRightFixedWidth,
    position: { x: rightStartX + frameThickness, y: rightFixed?.position?.y || 1100, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_horizontal_frame_profile_2', {
    width: newMiddleHorizontal2Width,
    position: {
      x: rightStartX + frameThickness,
      y: middleHorizontal2?.position?.y || 1040,
      z: 0,
    } as ModelParameter['position'],
  })

  // Right section window frames
  updateParam(model, 'top_window_2_left_frame_profile', {
    position: { x: rightStartX + frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_2_right_frame_profile', {
    position: { x: rightStartX + 120 + newRightOpenableWidth, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_2_top_frame_profile', {
    width: newRightFixedWidth,
    position: { x: rightStartX + frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_2_bottom_frame_profile', {
    width: newRightFixedWidth,
    position: {
      x: rightStartX + frameThickness,
      y: getParam(model.parameters!, 'top_window_2_bottom_frame_profile')?.position?.y || 980,
      z: 0,
    } as ModelParameter['position'],
  })

  // Vertical dividers
  updateParam(model, 'left_vertical_frame_profile', {
    position: { x: newLeftWidth - frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_vertical_frame_profile', {
    position: { x: rightStartX, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_frame_profile', {
    position: { x: validatedValue - frameThickness, y: 0, z: 0 } as ModelParameter['position'],
  })

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const validatedValue = validateDimension('totalHeight', numValue)

  model.height = validatedValue

  updateParam(model, 'left_frame_profile', { height: validatedValue })
  updateParam(model, 'right_frame_profile', { height: validatedValue })
  updateParam(model, 'left_vertical_frame_profile', { height: validatedValue - frameThickness })
  updateParam(model, 'right_vertical_frame_profile', { height: validatedValue - frameThickness })
  updateParam(model, 'middle_vertical_frame_profile_center', { height: validatedValue - frameThickness - frameThickness })

  if (!cascadeUpdates) return model

  const currentWindowFrame = getParam(model.parameters!, 'top_window_1_left_frame_profile')
  const currentFixedPanel = getParam(model.parameters!, 'left_glass_panel')
  const currentWindowFrameHeight = currentWindowFrame?.height || 980
  const currentFixedPanelHeight = currentFixedPanel?.height || 1040

  const availableHeight = validatedValue - frameThickness - frameThickness - frameThickness
  const heightDifference = availableHeight - (currentWindowFrameHeight + currentFixedPanelHeight)
  const windowFrameHeight = currentWindowFrameHeight + heightDifference / 2
  const fixedPanelHeight = currentFixedPanelHeight + heightDifference / 2
  const openableGlassHeight = windowFrameHeight - frameThickness - frameThickness

  // Left section
  const leftOpenable = getParam(model.parameters!, 'left_glass_panel_opanable')
  const leftFixed = getParam(model.parameters!, 'left_glass_panel')
  updateParam(model, 'left_glass_panel_opanable', {
    height: openableGlassHeight,
    position: { x: leftOpenable?.position?.x || 120, y: frameThickness + frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'left_glass_panel', {
    height: fixedPanelHeight,
    position: { x: leftFixed?.position?.x || frameThickness, y: frameThickness + windowFrameHeight + frameThickness, z: 0 } as ModelParameter['position'],
  })

  // Right section
  const rightOpenable = getParam(model.parameters!, 'right_glass_panel_opanable')
  const rightFixed = getParam(model.parameters!, 'right_glass_panel')
  updateParam(model, 'right_glass_panel_opanable', {
    height: openableGlassHeight,
    position: { x: rightOpenable?.position?.x || 3020, y: frameThickness + frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_glass_panel', {
    height: fixedPanelHeight,
    position: { x: rightFixed?.position?.x || 2960, y: frameThickness + windowFrameHeight + frameThickness, z: 0 } as ModelParameter['position'],
  })

  // Middle door panels
  const doorHeight = validatedValue - frameThickness - frameThickness
  updateParam(model, 'middle_glass_panel_1', { height: doorHeight })
  updateParam(model, 'middle_glass_panel_2', { height: doorHeight })

  // Window frame heights
  updateParam(model, 'top_window_1_left_frame_profile', { height: windowFrameHeight })
  updateParam(model, 'top_window_1_right_frame_profile', { height: windowFrameHeight })
  updateParam(model, 'top_window_2_left_frame_profile', { height: windowFrameHeight })
  updateParam(model, 'top_window_2_right_frame_profile', { height: windowFrameHeight })

  // Window bottom frame positions
  const windowBottomY = frameThickness + windowFrameHeight - frameThickness
  const topWindow1Bottom = getParam(model.parameters!, 'top_window_1_bottom_frame_profile')
  const topWindow2Bottom = getParam(model.parameters!, 'top_window_2_bottom_frame_profile')
  updateParam(model, 'top_window_1_bottom_frame_profile', {
    position: { x: topWindow1Bottom?.position?.x || frameThickness, y: windowBottomY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_2_bottom_frame_profile', {
    position: { x: topWindow2Bottom?.position?.x || 2960, y: windowBottomY, z: 0 } as ModelParameter['position'],
  })

  // Hinges and handles
  updateHingesProportionally(model, 'top_window_1_left_frame_profile', currentWindowFrameHeight, windowFrameHeight)
  updateHingesProportionally(model, 'top_window_2_right_frame_profile', currentWindowFrameHeight, windowFrameHeight)

  // Bottom frame positions
  const bottomY = validatedValue - frameThickness
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomCenter = getParam(model.parameters!, 'bottom_center_frame_profile_1')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  updateParam(model, 'bottom_left_frame_profile', {
    position: { x: bottomLeft?.position?.x || 0, y: bottomY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'bottom_center_frame_profile_1', {
    position: { x: bottomCenter?.position?.x || 700, y: bottomY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'bottom_right_frame_profile', {
    position: { x: bottomRight?.position?.x || 2900, y: bottomY, z: 0 } as ModelParameter['position'],
  })

  // Middle horizontal frame positions
  const middleY = frameThickness + windowFrameHeight
  const middleHorizontal1 = getParam(model.parameters!, 'middle_horizontal_frame_profile_1')
  const middleHorizontal2 = getParam(model.parameters!, 'middle_horizontal_frame_profile_2')
  updateParam(model, 'middle_horizontal_frame_profile_1', {
    position: { x: middleHorizontal1?.position?.x || frameThickness, y: middleY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_horizontal_frame_profile_2', {
    position: { x: middleHorizontal2?.position?.x || 2960, y: middleY, z: 0 } as ModelParameter['position'],
  })

  return model
}

const doorPanelHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const doorHeight = validateDimension('doorPanelHeight', numValue)

  updateParam(model, 'door_glass_panel_1', { height: doorHeight })
  updateParam(model, 'door_glass_panel_2', { height: doorHeight })

  if (!cascadeUpdates) return model

  const newTotalHeight = doorHeight + 80
  const validatedTotalHeight = validateDimension('totalHeight', newTotalHeight)
  model.height = validatedTotalHeight

  updateParam(model, 'left_frame_profile', { height: validatedTotalHeight })
  updateParam(model, 'right_frame_profile', { height: validatedTotalHeight })
  updateParam(model, 'left_vertical_frame_profile', { height: validatedTotalHeight - 80 })
  updateParam(model, 'right_vertical_frame_profile', { height: validatedTotalHeight - 80 })
  updateParam(model, 'middle_vertical_frame_profile_center', { height: validatedTotalHeight - 80 })

  const bottomY = validatedTotalHeight - 60
  updateParam(model, 'bottom_left_frame_profile', { position: { x: 0, y: bottomY, z: 0 } as ModelParameter['position'] })
  updateParam(model, 'bottom_center_frame_profile_1', {
    position: {
      x: getParam(model.parameters!, 'bottom_center_frame_profile_1')?.position?.x || 700,
      y: bottomY,
      z: 0,
    } as ModelParameter['position'],
  })
  updateParam(model, 'bottom_right_frame_profile', {
    position: {
      x: getParam(model.parameters!, 'bottom_right_frame_profile')?.position?.x || 2900,
      y: bottomY,
      z: 0,
    } as ModelParameter['position'],
  })

  return model
}

const bottomLeftWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const leftWidth = numValue

  const centerWidth = getParam(model.parameters!, 'bottom_center_frame_profile_1')?.width || 2200
  const rightWidth = getParam(model.parameters!, 'bottom_right_frame_profile')?.width || 700
  const newTotalWidth = leftWidth + centerWidth + rightWidth
  const validatedTotalWidth = validateDimension('totalWidth', newTotalWidth)

  model.width = validatedTotalWidth
  updateParam(model, 'top_frame_profile', { width: validatedTotalWidth })
  updateParam(model, 'bottom_left_frame_profile', { width: leftWidth })

  if (!cascadeUpdates) return model

  const leftOpenable = getParam(model.parameters!, 'left_glass_panel_opanable')
  const leftFixed = getParam(model.parameters!, 'left_glass_panel')
  const middleHorizontal1 = getParam(model.parameters!, 'middle_horizontal_frame_profile_1')

  const leftOpenableOffset = 120
  const newLeftOpenableWidth = leftWidth - leftOpenableOffset - frameThickness * 2
  const newLeftFixedWidth = leftWidth - frameThickness * 2
  const newMiddleHorizontal1Width = leftWidth - frameThickness * 2

  updateParam(model, 'left_glass_panel_opanable', {
    width: newLeftOpenableWidth,
    position: { x: leftOpenableOffset, y: leftOpenable?.position?.y || 120, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'left_glass_panel', {
    width: newLeftFixedWidth,
    position: { x: frameThickness, y: leftFixed?.position?.y || 1100, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_horizontal_frame_profile_1', {
    width: newMiddleHorizontal1Width,
    position: { x: frameThickness, y: middleHorizontal1?.position?.y || 1040, z: 0 } as ModelParameter['position'],
  })

  // Left section window frames
  updateParam(model, 'top_window_1_left_frame_profile', {
    position: { x: frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_right_frame_profile', {
    position: { x: leftWidth - frameThickness * 2, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_top_frame_profile', {
    width: newLeftFixedWidth,
    position: { x: frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_bottom_frame_profile', {
    width: newLeftFixedWidth,
    position: {
      x: frameThickness,
      y: getParam(model.parameters!, 'top_window_1_bottom_frame_profile')?.position?.y || 980,
      z: 0,
    } as ModelParameter['position'],
  })

  // Recalculate positions
  const centerStartX = leftWidth
  const rightStartX = leftWidth + centerWidth

  updateParam(model, 'left_vertical_frame_profile', {
    position: { x: leftWidth - frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_vertical_frame_profile', {
    position: { x: rightStartX, y: frameThickness, z: 0 } as ModelParameter['position'],
  })

  // Center section positions
  updateParam(model, 'bottom_center_frame_profile_1', {
    position: { x: centerStartX, y: (model.height as number) - frameThickness, z: 0 } as ModelParameter['position'],
  })

  const doorWidth = (centerWidth - frameThickness) / 2
  updateParam(model, 'middle_glass_panel_1', {
    width: doorWidth,
    position: { x: centerStartX, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_glass_panel_2', {
    width: doorWidth,
    position: { x: centerStartX + doorWidth + frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_vertical_frame_profile_center', {
    position: { x: centerStartX + doorWidth, y: frameThickness, z: 0 } as ModelParameter['position'],
  })

  // Right section positions
  const rightOpenable = getParam(model.parameters!, 'right_glass_panel_opanable')
  const rightFixed = getParam(model.parameters!, 'right_glass_panel')
  const middleHorizontal2 = getParam(model.parameters!, 'middle_horizontal_frame_profile_2')

  updateParam(model, 'bottom_right_frame_profile', {
    position: { x: rightStartX, y: (model.height as number) - frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_glass_panel_opanable', {
    position: { x: rightStartX + 120, y: rightOpenable?.position?.y || 120, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_glass_panel', {
    position: { x: rightStartX + frameThickness, y: rightFixed?.position?.y || 1100, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_horizontal_frame_profile_2', {
    position: { x: rightStartX + frameThickness, y: middleHorizontal2?.position?.y || 1040, z: 0 } as ModelParameter['position'],
  })

  // Right window frames
  updateParam(model, 'top_window_2_left_frame_profile', {
    position: { x: rightStartX + frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_2_right_frame_profile', {
    position: {
      x: rightStartX + 120 + (rightOpenable?.width || 460),
      y: frameThickness,
      z: 0,
    } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_2_top_frame_profile', {
    position: { x: rightStartX + frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_2_bottom_frame_profile', {
    position: {
      x: rightStartX + frameThickness,
      y: getParam(model.parameters!, 'top_window_2_bottom_frame_profile')?.position?.y || 980,
      z: 0,
    } as ModelParameter['position'],
  })

  updateParam(model, 'right_frame_profile', {
    position: { x: validatedTotalWidth - frameThickness, y: 0, z: 0 } as ModelParameter['position'],
  })

  return model
}

const bottomCenterWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const centerWidth = numValue

  const leftWidth = getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 700
  const rightWidth = getParam(model.parameters!, 'bottom_right_frame_profile')?.width || 700
  const newTotalWidth = leftWidth + centerWidth + rightWidth
  const validatedTotalWidth = validateDimension('totalWidth', newTotalWidth)

  model.width = validatedTotalWidth
  updateParam(model, 'top_frame_profile', { width: validatedTotalWidth })
  updateParam(model, 'bottom_center_frame_profile_1', { width: centerWidth })

  if (!cascadeUpdates) return model

  const centerStartX = leftWidth
  const rightStartX = leftWidth + centerWidth
  const doorWidth = (centerWidth - frameThickness) / 2

  updateParam(model, 'bottom_center_frame_profile_1', {
    position: { x: centerStartX, y: (model.height as number) - frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_glass_panel_1', {
    width: doorWidth,
    position: { x: centerStartX, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_glass_panel_2', {
    width: doorWidth,
    position: { x: centerStartX + doorWidth + frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_vertical_frame_profile_center', {
    position: { x: centerStartX + doorWidth, y: frameThickness, z: 0 } as ModelParameter['position'],
  })

  // Right section positions
  const rightOpenable = getParam(model.parameters!, 'right_glass_panel_opanable')
  const rightFixed = getParam(model.parameters!, 'right_glass_panel')
  const middleHorizontal2 = getParam(model.parameters!, 'middle_horizontal_frame_profile_2')

  updateParam(model, 'bottom_right_frame_profile', {
    position: { x: rightStartX, y: (model.height as number) - frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_glass_panel_opanable', {
    position: { x: rightStartX + 120, y: rightOpenable?.position?.y || 120, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_glass_panel', {
    position: { x: rightStartX + frameThickness, y: rightFixed?.position?.y || 1100, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_vertical_frame_profile', {
    position: { x: rightStartX, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_horizontal_frame_profile_2', {
    position: { x: rightStartX + frameThickness, y: middleHorizontal2?.position?.y || 1040, z: 0 } as ModelParameter['position'],
  })

  // Right window frames
  updateParam(model, 'top_window_2_left_frame_profile', {
    position: { x: rightStartX + frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_2_right_frame_profile', {
    position: {
      x: rightStartX + 120 + (rightOpenable?.width || 460),
      y: frameThickness,
      z: 0,
    } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_2_top_frame_profile', {
    position: { x: rightStartX + frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_2_bottom_frame_profile', {
    position: {
      x: rightStartX + frameThickness,
      y: getParam(model.parameters!, 'top_window_2_bottom_frame_profile')?.position?.y || 980,
      z: 0,
    } as ModelParameter['position'],
  })

  updateParam(model, 'right_frame_profile', {
    position: { x: validatedTotalWidth - frameThickness, y: 0, z: 0 } as ModelParameter['position'],
  })

  return model
}

const bottomRightWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const rightWidth = numValue

  const leftWidth = getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 700
  const centerWidth = getParam(model.parameters!, 'bottom_center_frame_profile_1')?.width || 2200
  const newTotalWidth = leftWidth + centerWidth + rightWidth
  const validatedTotalWidth = validateDimension('totalWidth', newTotalWidth)

  model.width = validatedTotalWidth
  updateParam(model, 'top_frame_profile', { width: validatedTotalWidth })
  updateParam(model, 'bottom_right_frame_profile', { width: rightWidth })

  if (!cascadeUpdates) return model

  const rightStartX = leftWidth + centerWidth
  const rightOpenable = getParam(model.parameters!, 'right_glass_panel_opanable')
  const rightFixed = getParam(model.parameters!, 'right_glass_panel')
  const middleHorizontal2 = getParam(model.parameters!, 'middle_horizontal_frame_profile_2')

  const rightOpenableOffset = 120
  const newRightOpenableWidth = rightWidth - rightOpenableOffset - frameThickness * 2
  const newRightFixedWidth = rightWidth - frameThickness * 2
  const newMiddleHorizontal2Width = rightWidth - frameThickness * 2

  updateParam(model, 'right_glass_panel_opanable', {
    width: newRightOpenableWidth,
    position: { x: rightStartX + rightOpenableOffset, y: rightOpenable?.position?.y || 120, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_glass_panel', {
    width: newRightFixedWidth,
    position: { x: rightStartX + frameThickness, y: rightFixed?.position?.y || 1100, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_horizontal_frame_profile_2', {
    width: newMiddleHorizontal2Width,
    position: { x: rightStartX + frameThickness, y: middleHorizontal2?.position?.y || 1040, z: 0 } as ModelParameter['position'],
  })

  // Right section window frames
  updateParam(model, 'top_window_2_left_frame_profile', {
    position: { x: rightStartX + frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_2_right_frame_profile', {
    position: { x: rightStartX + rightWidth - frameThickness * 2, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_2_top_frame_profile', {
    width: newRightFixedWidth,
    position: { x: rightStartX + frameThickness, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_2_bottom_frame_profile', {
    width: newRightFixedWidth,
    position: {
      x: rightStartX + frameThickness,
      y: getParam(model.parameters!, 'top_window_2_bottom_frame_profile')?.position?.y || 980,
      z: 0,
    } as ModelParameter['position'],
  })

  updateParam(model, 'bottom_right_frame_profile', {
    position: { x: rightStartX, y: (model.height as number) - frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_vertical_frame_profile', {
    position: { x: rightStartX, y: frameThickness, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_frame_profile', {
    position: { x: validatedTotalWidth - frameThickness, y: 0, z: 0 } as ModelParameter['position'],
  })

  return model
}

const rightOpenablePanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const validatedValue = validateDimension('windowHeight', numValue)
  handleRedistributiveHeight(model, 'rightOpenablePanelHeight', validatedValue)
  return model
}

const rightFixedPanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const validatedValue = validateDimension('windowHeight', numValue)
  handleRedistributiveHeight(model, 'rightFixedPanelHeight', validatedValue)
  return model
}

const leftOpenablePanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const validatedValue = validateDimension('windowHeight', numValue)
  handleRedistributiveHeight(model, 'leftOpenablePanelHeight', validatedValue)
  return model
}

const leftFixedPanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const validatedValue = validateDimension('windowHeight', numValue)
  handleRedistributiveHeight(model, 'leftFixedPanelHeight', validatedValue)
  return model
}

export const model16DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  doorPanelHeight,
  bottomLeftWidth,
  bottomCenterWidth,
  bottomRightWidth,
  rightOpenablePanelHeight,
  rightFixedPanelHeight,
  leftOpenablePanelHeight,
  leftFixedPanelHeight,
}
